import { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight } from "lucide-react";
import type { PVValidationCheck } from "@/types";

interface ValidationCheckRowProps {
	check: PVValidationCheck;
	defaultExpanded?: boolean;
}

const STATUS_CONFIG = {
	pass: { icon: CheckCircle2, color: "text-green-500", label: "✓" },
	warn: { icon: AlertTriangle, color: "text-yellow-500", label: "⚠" },
	fail: { icon: XCircle, color: "text-red-500", label: "✗" },
} as const;

function formatValue(value: unknown): string {
	if (value === null || value === undefined) return "—";
	if (typeof value === "object") return JSON.stringify(value, null, 2);
	return String(value);
}

export function ValidationCheckRow({ check, defaultExpanded }: ValidationCheckRowProps) {
	const [expanded, setExpanded] = useState(
		defaultExpanded ?? check.status === "fail",
	);

	const { icon: StatusIcon, color } = STATUS_CONFIG[check.status];
	const hasDetails =
		check.details?.config ||
		check.details?.formula ||
		check.details?.steps ||
		(check.expected !== undefined && check.actual !== undefined);

	return (
		<div className="border rounded-md">
			<button
				type="button"
				className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
				onClick={() => setExpanded((prev) => !prev)}
			>
				{hasDetails ? (
					expanded ? (
						<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
					) : (
						<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
					)
				) : (
					<span className="w-4 shrink-0" />
				)}
				<StatusIcon className={`h-4 w-4 shrink-0 ${color}`} />
				<span className="font-medium text-sm">{check.name}</span>
				<span className="text-sm text-muted-foreground ml-auto">
					{check.message}
				</span>
			</button>

			{expanded && hasDetails && (
				<div className="px-3 pb-3 pt-1 ml-11 space-y-3 text-sm border-t">
					{check.details?.config && (
						<div>
							<p className="font-medium text-muted-foreground mb-1">
								Config Resolution
							</p>
							<div className="grid grid-cols-2 gap-x-4 gap-y-1">
								{Object.entries(check.details.config).map(
									([key, value]) => (
										<div key={key} className="contents">
											<span className="font-mono text-xs text-muted-foreground">
												{key}
											</span>
											<span className="font-mono text-xs">
												{formatValue(value)}
											</span>
										</div>
									),
								)}
							</div>
						</div>
					)}

					{check.details?.formula && (
						<div>
							<p className="font-medium text-muted-foreground mb-1">
								Formula
							</p>
							<pre className="bg-muted p-2 rounded text-xs font-mono whitespace-pre-wrap">
								{check.details.formula}
							</pre>
						</div>
					)}

					{check.details?.steps && check.details.steps.length > 0 && (
						<div>
							<p className="font-medium text-muted-foreground mb-1">
								Steps
							</p>
							<ol className="list-decimal list-inside space-y-0.5 text-xs font-mono">
								{check.details.steps.map((step, idx) => (
									<li key={`${check.id}-step-${idx}`}>
										{step}
									</li>
								))}
							</ol>
						</div>
					)}

					{check.expected !== undefined &&
						check.actual !== undefined && (
							<div>
								<p className="font-medium text-muted-foreground mb-1">
									Expected vs Actual
								</p>
								<div className="grid grid-cols-2 gap-4">
									<div className="bg-muted p-2 rounded">
										<span className="text-xs text-muted-foreground block mb-1">
											Expected
										</span>
										<span className="font-mono text-xs">
											{formatValue(check.expected)}
										</span>
									</div>
									<div
										className={`p-2 rounded ${
											formatValue(check.expected) !==
											formatValue(check.actual)
												? "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
												: "bg-muted"
										}`}
									>
										<span className="text-xs text-muted-foreground block mb-1">
											Actual
										</span>
										<span className="font-mono text-xs">
											{formatValue(check.actual)}
										</span>
									</div>
								</div>
							</div>
						)}
				</div>
			)}
		</div>
	);
}
